import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
    Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis
} from 'recharts';
import { BreakdownResponse } from '../../services/caseWorkflowService';
import { PROCESSED_AXIS_CAPTION, count } from './caseWorkflowVocabulary';
import {
    useChartChrome, MAX_SERIES, NULL_BUCKET_COLOUR, OTHER_COLOUR,
    byPriority, priorityColour, priorityLabel, seriesColour
} from './caseWorkflowPalette';
import { useThemeMode } from '../../theme/ThemeModeProvider';
import { dsVar } from '../../theme/tokens';

interface TrendChartProps {
    data: BreakdownResponse;
    height?: number;
}

const isPriority = (dimension: string) => dimension === 'priority' || dimension === 'machine_priority';

const bucketLabel = (iso: string, bucket: string | null): string => {
    // The value is a label already formatted in the reporting timezone, so it is
    // sliced rather than parsed — re-reading it as a Date would drag it back
    // into the browser's zone and move counts across day boundaries.
    const date = iso.slice(0, 10);
    if (bucket === 'month') return date.slice(0, 7);
    return date;
};

/**
 * Volume over time, optionally split by a dimension.
 *
 * Stacked bars rather than lines: the buckets are discrete periods and the
 * question is "how much, made up of what", not "what is the trajectory".
 *
 * Two labels here are mandatory and neither is decoration. The axis says
 * PROCESSED, because event_at is when the platform moved the case — there is no
 * business or transaction date on a live case record at all, and an axis
 * captioned "alert date" would assert a date the platform does not hold. And
 * the timezone is named, because AML reporting is jurisdictional and a silent
 * default moves counts across day boundaries.
 */
const TrendChart: React.FC<TrendChartProps> = ({ data, height = 260 }) => {
    const { resolved } = useThemeMode();
    const chrome = useChartChrome();
    const { chartData, series } = useMemo(() => {
        const values = new Map<string, number>();
        for (const row of data.rows) {
            const key = row.dimensionValue ?? '__null__';
            const magnitude = (data.level === 'case' ? row.cases : row.transitions) ?? 0;
            values.set(key, (values.get(key) ?? 0) + magnitude);
        }

        // Ordered by total so colour follows the entity rather than its position
        // in any one bucket — a series must not change colour between renders.
        let ordered = [...values.entries()].sort((a, b) => b[1] - a[1]).map(([key]) => key);
        if (isPriority(data.dimension)) ordered = ordered.sort(byPriority);

        const kept = ordered.slice(0, MAX_SERIES);
        const folded = ordered.slice(MAX_SERIES);

        // The axis comes from the API and carries every period in the window,
        // including ones with no activity. This project has no events at all on
        // one of its three days; a chart that closes that gap misstates it.
        const axis = data.buckets ?? [...new Set(data.rows.map((r) => r.bucket).filter(Boolean) as string[])].sort();

        const rowsByBucket = new Map<string, Record<string, number>>();
        for (const bucket of axis) rowsByBucket.set(bucket, {});
        for (const row of data.rows) {
            if (!row.bucket) continue;
            const target = rowsByBucket.get(row.bucket);
            if (!target) continue;
            const key = row.dimensionValue ?? '__null__';
            const seriesKey = kept.includes(key) ? key : '__other__';
            const magnitude = (data.level === 'case' ? row.cases : row.transitions) ?? 0;
            target[seriesKey] = (target[seriesKey] ?? 0) + magnitude;
        }

        return {
            chartData: axis.map((bucket) => ({
                bucket,
                label: bucketLabel(bucket, data.bucket),
                ...rowsByBucket.get(bucket)
            })),
            series: folded.length ? [...kept, '__other__'] : kept
        };
    }, [data]);

    const labelFor = (key: string): string => {
        if (key === '__null__') return 'Not recorded';
        if (key === '__other__') return 'Other';
        if (isPriority(data.dimension) && /^P[1-5]$/.test(key)) return priorityLabel(key);
        if (key === data.nullBucket) {
            return key === 'unassigned' ? 'Not yet assigned'
                : key === 'undeclared' ? 'No workflow declared'
                : key === 'unrecorded' ? 'Not recorded'
                : key;
        }
        return key.replace(/_/g, ' ');
    };

    const colourFor = (key: string, index: number): string => {
        if (key === '__other__') return OTHER_COLOUR;
        if (key === '__null__' || key === data.nullBucket) return NULL_BUCKET_COLOUR;
        if (isPriority(data.dimension)) return priorityColour(key, resolved);
        return seriesColour(index);
    };

    if (!chartData.length) {
        return (
            <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-muted'), fontStyle: 'italic' }}>
                No activity in this window.
            </Typography>
        );
    }

    return (
        <Box>
            <ResponsiveContainer width="100%" height={height}>
                <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 4, left: -12 }}>
                    <CartesianGrid stroke={chrome.grid} strokeDasharray="3 3" vertical={false} />
                    <XAxis
                        dataKey="label"
                        tick={{ fill: chrome.axisText, fontSize: 11 }}
                        stroke={chrome.grid}
                        tickLine={false}
                    />
                    <YAxis
                        tick={{ fill: chrome.axisText, fontSize: 11 }}
                        stroke={chrome.grid}
                        tickLine={false}
                        axisLine={false}
                    />
                    <Tooltip
                        cursor={{ fill: 'rgba(148,163,184,0.08)' }}
                        contentStyle={{
                            backgroundColor: chrome.surface,
                            border: `1px solid ${chrome.grid}`,
                            borderRadius: 8,
                            fontSize: 12
                        }}
                        labelStyle={{ color: dsVar('fg-soft') }}
                        formatter={(value: any, key: any) => [count(Number(value)), labelFor(String(key))]}
                    />
                    {/* Always present for two or more series, so identity is never colour alone. */}
                    {series.length > 1 && (
                        <Legend
                            wrapperStyle={{ fontSize: 11, color: chrome.axisText }}
                            formatter={(key) => labelFor(String(key))}
                        />
                    )}
                    {series.map((key, index) => (
                        <Bar
                            key={key}
                            dataKey={key}
                            stackId="a"
                            fill={colourFor(key, index)}
                            // A 2px surface gap between stacked segments.
                            stroke={chrome.surface}
                            strokeWidth={2}
                            radius={index === series.length - 1 ? [4, 4, 0, 0] : 0}
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>

            <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted'), mt: 1 }}>
                {PROCESSED_AXIS_CAPTION(data.tz || 'UTC')}
            </Typography>
        </Box>
    );
};

export default TrendChart;
